/**
 * Polly's own texts - the catalogue the Polly app adds to liqui-loc's global one.
 *
 * Polly localises exactly like LIQUIDO, with liqui-loc (src/shared/loc/liqui-loc.js): src/polly/main.js passes
 * [globalMessages, pollyMessages] to createLoc(), and every Polly component uses $t() or useLoc(). This table holds
 * the texts only Polly has; the words both apps use (Ok, Cancel, Save, Next, ...) are in
 * src/shared/loc/global-messages.js.
 */

const messages = {
	en: {
		// creating
		PollyTitlePlaceholder: "What shall we decide?",
		// the hints that fade into one another in the empty question, see polly-page.vue
		PollyTitleHint1: "What do you want to vote on?",
		PollyTitleHint2: "What is up for decision?",
		PollyTitleHint3: "What do you want to decide?",
		PollyTitleHint4: "Whom do you want to elect?",
		PollyTitleInvalidFeedback: "A bit longer please, at least {minLength} characters.",
		PollyOptionsInvalidFeedback: "A poll needs at least two proposals (or candidates).",
		AddProposalPlaceholder: "Add another option",
		// how a polly works, the list under a new polly
		HowItWorks1: "This is a polly - a small, quick, simple & private vote among friends.",
		HowItWorks2: "We create a secure biometric passkey for you (Face ID/fingerprint). That makes you the polly's admin - no password needed.",
		HowItWorks3: "A clever algorithm then calculates the winner with the most approval among all of you.",
		HowItWorks4: "Share your polly with your friends, and they can vote.",
		HowLiquidoBefore: "Do you want real anonymous votes? Then have a look at",
		HowLiquidoAfter: "instead.",
		HowItWorks5: "You have to trust your friends not to vote several times under a false name.",

		// voting
		CastVote: "Vote",
		ConfirmVoteButton: "Yes, cast my vote",
		AlreadyVoted: "You already voted. Thanks!",

		// admin
		LinkCopied: "Link copied. Now send it to your friends!",

		// finished
		PollyFinished: "Finished — 1 vote | Finished — {n} votes",
		Winner: "Winner",

		// trouble
		PasskeyRequirement: "I am sorry. To create a polly you need a phone that supports passkeys (Face-ID or fingerprint). Only that way can Polly be private and secure.",
		PollyNotFound: "This polly does not exist (any more).",
		PageNotFound: "Oops, there is no such page. I must have flown the wrong way!",
		PageNotFoundTitle: "Oops",
		CannotSave: "Sorry, that did not work. Please try again.",
		CannotEditAnymore: "Someone already voted, so the options cannot be changed any more.",
		NotOwner: "Only the person who created this polly can do that.",

		// honesty about the privacy model - a polly is not a LIQUIDO poll
		PrivacyNote: "A polly is private among friends. For a truly anonymous ballot, use a LIQUIDO poll.",

		// the creating wizard (polly-page.vue), step 2
		OwnerNameLabel: "Your (nick)name",
		OwnerEmailLabel: "Your email",
		OwnerCardTitle: "About you",
		OwnerEmailInvalid: "Please enter a valid email address.",
		OwnerNameMissing: "Please enter your name.",
		OwnerEmailMissing: "Please enter your email address.",
		PasskeyMissing: "Please create your passkey first.",
		PasskeyExplain: "You already use a passkey every day to unlock your phone. We use the same secure method to protect your polly. That is how you become the admin.",
		CreatePasskey: "Create passkey",
		RegisterPasskey: "Register passkey",
		PasskeyReady: "Passkey is ready",
		NotYou: "You are not {name}?",
		NotYouNoName: "Not you?",
		LogoutOnThisDevice: "Log in again",
		PetNextVoter: "Does somebody else want to vote?",
		PasskeyReadyAfterVote: "Your passkey is ready. You can come back to this polly any time - and start your own.",
		StartPollyNow: "Start polly",
		OwnerNameTooShort: "At least {min} characters, please.",
		OwnerEmailConfirmed: "Confirmed - the address of your account.",

		// confirming the email (polly-confirm-email.vue), the link in the link mail
		ConfirmEmailTitle: "Confirm email",
		ConfirmingEmail: "Confirming your email address ...",
		ConfirmEmailNeedPasskey: "Confirm with the passkey you started your polly with.",
		ConfirmWithPasskey: "Confirm with passkey",
		EmailConfirmed: "Your email address {email} is confirmed. Your pollys now belong to your account with this address.",
		EmailAlreadyConfirmed: "Your email address is already confirmed.",
		ConfirmWrongPasskey: "This link belongs to another passkey. Open it on the device you started the polly with - or choose that passkey.",
		ChooseOtherPasskey: "Choose another passkey",
		ConfirmLinkExpired: "This link has expired. The mail of your next polly brings a new one.",
		ConfirmLinkInvalid: "This link is not valid (any more). Only the link in your newest polly mail works.",
		ConfirmAccountUnverified: "There already is a LIQUIDO account with this address, but it is not confirmed yet. Confirm it first with the link in its welcome mail, then open this link again.",
		NoPasskeyToConfirm: "Your device did not confirm. Try again with the passkey you started the polly with.",

		// on the polly
		PollySticker: "Polly",   // the paper sticker on top of the ballot, polly-sticker.vue
		PollyBy: "Polly by {name}",
		VoterNameLabel: "Your name",
		PollyIsOver: "This polly has already ended.",

		// my pollys (pollys-list.vue)
		MyPollys: "All your pollys",
		BackToList: "Back to the list",
		NewPolly: "New Polly",
		CreateNewPolly: "Create new polly",
		PetMyPollys: "These are your pollys",
		MyPollysNeedPasskey: "Your pollys belong to your passkey - no account, no email. Confirm with your device to see them.",
		ShowMyPollys: "Show my pollys",
		NoPasskeyForPollys: "This device has no polly passkey yet. Just create your first polly!",
		NoPollysYet: "You have not created a polly yet.",
		NoPollysMatchSearch: "No polly matches your search.",
		CannotLoadPollys: "Your pollys could not be loaded. Please try again.",

		// Polly (polly-page.vue): Athena the owl speaks in short, friendly sentences
		PetLoading: "One moment, I'm fetching your polly ...",
		PetBusy: "Just a moment ...",
		PetMissing: "Hmm, I can't find this polly.",
		PetHelloCreate: "Hi! I'm Athena. What should your friends vote on?",
		PetAskOptions: "Nice question! Now give me at least two answers.",
		PetDuplicateOption: "\"{title}\" is in the list twice. Every answer may be there only once.",
		PetReadyForNext: "Looks good! Tap Next.",
		PetFieldTitle: "Ask something everyone can answer.",
		PetFieldOption: "One answer per line. New lines appear by themselves.",
		PetEditHint: "You can still change everything - until the first vote is in.",
		PetAboutName: "Almost done! What's your name? A nickname is fine.",
		PetAboutEmail: "Hi {name}! Where shall I send the link?",
		PetAboutPasskey: "Last thing, {name}: a passkey - your key to this polly.",
		PetFieldName: "A nickname is fine - your friends will see it.",
		PetFieldEmail: "I'll mail you the link. Your friends never see your address.",
		PetFieldEmailNamed: "Thanks, {name}! I'll mail you the link - only I see your address.",
		PetFieldPasskey: "Your passkey is your key - no password needed.",
		PetPasskeyIsReady: "Your passkey is ready!",
		PetReadyToStart: "All set! Start your polly.",
		PetSaved: "Saved!",
		PetShareReady: "Great, your polly is now live.",
		PetBallotFriend: "Hi! {name} has a question for you.",
		PetBallotOwner: "Your polly is running. Vote yourself, too!",
		PetBallotNoOwner: "Hi! Here's a question for you.",
		PetConfirm: "Sure, {name}? You can only vote once!",
		PetVoted: "Thanks, {name}! Your vote is in.",
		PetVotedWaitForOwner: "As soon as {name} finishes this polly, you will see the winner here.",
		PetWinner: "And the winner is: {title}!",
		PetNoVotes: "Nobody voted this time. Maybe next time!",
		PetNameMissing: "Tell me your name first, please.",
		PetNameTaken: "Someone already used this name here. Pick another one.",
		// The creator changed the polly while this ballot was open (polly-page.vue, takeChangedPolly)
		PollyChangedMeanwhile: "Sorry, {name} has just changed this polly. I am loading it again.",
		PetNeedPasskey: "I need your passkey for that. Tap Create passkey.",
		PetPasskeyFailed: "That didn't work. Please try the passkey again.",
		PetCopyFailed: "I couldn't copy it. Press and hold the link instead.",
		PetGiggle1: "Uuhuu! Uhhhuu!",
		PetGiggle2: "You seek my attention, yet true wisdom is found in listening, not poking.",
		PetGiggle3: "To touch the surface is easy; to understand the depths takes a lifetime",
		PetMoved: "{title} is now number {n}",
		PetQuestionLabel: "Your question",
		PetOptionLabel: "Answer {n}",
		PetRemoveOption: "Remove answer {n}",
		PetHowTitle: "How it works",
		PetHow1: "Put all options in your order - your favourite on top.",
		PetHow2: "Everybody votes once. The option most people can live with wins.",
		PetHow3: "Everybody sees who voted - but never how.",
		PetFlipInfo: "Info",
		PetFlipBack: "Back",
		PetCardOpen: "Show the options",
		PetCardClose: "Hide the options",
		PetNotebook: "Athena's notebook",
		StepVote: "Vote",
		PetRankHint: "Sort all answers\nwith your favourite at the very top.",
		PetRankList: "Your ranking",
		PetRankUp: "Move {title} up",
		PetRankDown: "Move {title} down",
		PetConfirmVoteButton: "Cast this vote now",
		PetVoteBack: "back",
		PetShareTitle: "Share this one link",
		PetQrAlt: "QR code with the link to this polly",
		PetShareLinkButton: "Share link",
		PetFinishPollyLong: "Finish polly",
		PetEditPollyLong: "Edit polly",
		PetOnlyForYou: "Only for you",
		PetEditBeforeLink: "As long as nobody has voted, you can still",
		PetEditLinkText: "edit your polly",
		PetVoteNow: "Vote now",
		PetDone: "Done",
		PetLinkMailed: "I also sent you an email with all this info.",
		PetVotersTitle: "Who has voted",
		PetNobodyYet: "Nobody yet.",
		PetYourVoteIn: "Thanks. Your vote is counted.",
		PetRegisterAfterVote: "With a passkey you can come back later and start your own polly.",
		PetConfirmFinish: "Really finish? Then nobody can vote any more.",
		PetYesFinish: "Yes, finish",
		PetOtherOptions: "The other proposals were:",
		PetStartOwn: "Start your own polly",
		PetStartAnother: "Start another polly",
	},
	de: {
		// creating
		PollyTitlePlaceholder: "Was steht zur Wahl?",
		PollyTitleHint1: "Worüber wollt ihr abstimmen?",
		PollyTitleHint2: "Was steht zur Wahl?",
		PollyTitleHint3: "Was wollt ihr entscheiden?",
		PollyTitleHint4: "Wen wollt ihr wählen?",
		PollyTitleInvalidFeedback: "Bitte mind. {minLength} Zeichen",
		PollyOptionsInvalidFeedback: "Ein Polly braucht mindestens zwei Vorschläge (oder Kandidaten).",
		AddProposalPlaceholder: "Weitere Option hinzufügen",
		// how a polly works, the list under a new polly
		HowItWorks1: "Ein Polly ist eine einfache und liquide Abstimmung unter Freunden.",
		HowItWorks2: "Hier stimmt ihr nicht nur für eine Antwort, sondern jeder ordnet alle Vorschläge in seine bevorzugte Reihenfolge. Deine Stimme bleibt dabei anonym.",
		HowItWorks3: "Ein fairer Algorithmus findet dann den Gewinner mit der größten Zustimmung.",
		HowItWorks4: "Deine Pollys werden mit einem biometrischen Passkey geschützt - ganz ohne Passwort.",
		HowLiquidoBefore: "Suchst ihr anonyme Abstimmungen? Dann schaut mal bei",
		HowLiquidoAfter: "vorbei.",
		HowItWorks5: "Bleibt fair! In einem Polly müsst ihr euch untereinander vertrauen, dass jeder mit seinem richtigen Namen abstimmt.",
		// voting
		CastVote: "Abstimmen",
		ConfirmVoteButton: "Ja, Stimme abgeben",
		AlreadyVoted: "Du hast abgestimmt. Danke!",

		// admin
		LinkCopied: "Link kopiert. Jetzt kannst du ihn deinen Freunden schicken!",

		// finished
		PollyFinished: "Beendet – 1 Stimme | Beendet – {n} Stimmen",
		Winner: "Gewinner",

		// trouble
		PasskeyRequirement: "Es tut mir leid, um ein Polly anlegen zu können, benötigst du ein Handy das Passkeys (Face-ID oder Fingerprint) unterstützt. Nur so kann Polly privat und sicher sein.",
		PollyNotFound: "Dieses Polly gibt es nicht (mehr).",
		PageNotFound: "Hoppla, diese Seite gibt es nicht. Da bin ich wohl falsch geflogen!",
		PageNotFoundTitle: "Ups",
		CannotSave: "Sorry, das hat nicht geklappt. Bitte versuch es noch einmal.",
		CannotEditAnymore: "Es wurde schon abgestimmt, die Optionen lassen sich nicht mehr ändern.",
		NotOwner: "Das kann nur die Person, die dieses Polly erstellt hat.",

		// honesty about the privacy model - a polly is not a LIQUIDO poll
		PrivacyNote: "Ein Polly ist privat unter Freunden. Für eine wirklich anonyme Wahl nimm eine LIQUIDO Abstimmung.",

		// the creating wizard (polly-page.vue), step 2
		OwnerNameLabel: "Dein (Spitz-)Name",
		OwnerEmailLabel: "Deine E-Mail",
		OwnerCardTitle: "Über dich",
		OwnerEmailInvalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
		OwnerNameMissing: "Bitte gib deinen Namen ein.",
		OwnerEmailMissing: "Bitte gib deine E-Mail-Adresse ein.",
		PasskeyMissing: "Bitte erstelle zuerst deinen Passkey.",
		PasskeyExplain: "Du benutzt bereits heute jeden Tag einen Passkey, um dein Handy zu entsperren. Wir nutzen das selbe sichere Verfahren um dein Polly zu schützen. So wirst du zum Admin.",
		CreatePasskey: "Passkey erstellen",
		RegisterPasskey: "Passkey registrieren",
		PasskeyReady: "Passkey ist bereit",
		NotYou: "Du bist nicht {name}?",
		NotYouNoName: "Nicht du?",
		LogoutOnThisDevice: "Melde dich neu an",
		PetNextVoter: "Möchte noch jemand abstimmen?",
		PasskeyReadyAfterVote: "Dein Passkey ist bereit. Du kannst jederzeit zu diesem Polly zurückkommen - und dein eigenes Polly starten.",
		StartPollyNow: "Polly starten",
		OwnerNameTooShort: "Bitte mindestens {min} Zeichen.",
		OwnerEmailConfirmed: "Bestätigt - die Adresse deines Kontos.",

		// confirming the email (polly-confirm-email.vue), the link in the link mail
		ConfirmEmailTitle: "E-Mail bestätigen",
		ConfirmingEmail: "Deine E-Mail-Adresse wird bestätigt …",
		ConfirmEmailNeedPasskey: "Bestätige mit dem Passkey, mit dem du dein Polly gestartet hast.",
		ConfirmWithPasskey: "Mit Passkey bestätigen",
		EmailConfirmed: "Deine E-Mail-Adresse {email} ist bestätigt. Deine Pollys gehören jetzt zu deinem Konto mit dieser Adresse.",
		EmailAlreadyConfirmed: "Deine E-Mail-Adresse ist schon bestätigt.",
		ConfirmWrongPasskey: "Dieser Link gehört zu einem anderen Passkey. Öffne ihn auf dem Gerät, mit dem du das Polly gestartet hast - oder wähle diesen Passkey.",
		ChooseOtherPasskey: "Anderen Passkey wählen",
		ConfirmLinkExpired: "Dieser Link ist abgelaufen. Mit der Mail zu deinem nächsten Polly bekommst du einen neuen.",
		ConfirmLinkInvalid: "Dieser Link ist nicht (mehr) gültig. Nur der Link aus deiner neuesten Polly-Mail funktioniert.",
		ConfirmAccountUnverified: "Zu dieser Adresse gibt es schon ein LIQUIDO-Konto, das noch nicht bestätigt ist. Bestätige es zuerst mit dem Link aus seiner Willkommens-Mail und öffne dann diesen Link noch einmal.",
		NoPasskeyToConfirm: "Dein Gerät hat nicht bestätigt. Versuch es noch einmal mit dem Passkey, mit dem du das Polly gestartet hast.",

		// on the polly
		PollySticker: "Polly",   // the paper sticker on top of the ballot, polly-sticker.vue
		PollyBy: "Polly von {name}",
		VoterNameLabel: "Dein Name",
		PollyIsOver: "Dieses Polly ist schon beendet.",

		// my pollys (pollys-list.vue)
		MyPollys: "Alle deine Pollys",
		BackToList: "Zurück zur Liste",
		NewPolly: "Neues Polly",
		CreateNewPolly: "Neues Polly erstellen",
		PetMyPollys: "Das sind deine Pollys",
		MyPollysNeedPasskey: "Deine Pollys gehören zu deinem Passkey - ganz ohne Account und E-Mail. Bestätige mit deinem Gerät, dann siehst du sie.",
		ShowMyPollys: "Meine Pollys zeigen",
		NoPasskeyForPollys: "Auf diesem Gerät gibt es noch keinen Polly-Passkey. Erstell einfach dein erstes Polly!",
		NoPollysYet: "Du hast noch kein Polly erstellt.",
		NoPollysMatchSearch: "Kein Polly passt zu deiner Suche.",
		CannotLoadPollys: "Deine Pollys konnten nicht geladen werden. Bitte versuch es noch einmal.",

		// Polly (polly-page.vue): Athena, die Eule, spricht in kurzen, freundlichen Sätzen
		PetLoading: "Moment, ich lade dein Polly …",
		PetBusy: "Einen Moment …",
		PetMissing: "Hmm, dieses Polly kann ich nicht finden.",
		PetHelloCreate: "Hallo! Ich bin Athena. Wollen wir gemeinsam ein Polly starten?",
		PetAskOptions: "Tolle Frage! Jetzt brauche ich mindestens zwei Antworten.",
		PetDuplicateOption: "\"{title}\" steht zweimal in der Liste. Jede Antwort darf nur einmal vorkommen.",
		PetReadyForNext: "Sieht gut aus! Tipp auf Weiter.",
		PetFieldTitle: "Worüber wollt ihr abstimmen?",
		PetFieldOption: "Eine Antwort pro Zeile. Weitere Zeilen kommen automatisch.",
		PetEditHint: "Du kannst noch alles ändern - bis die erste Stimme drin ist.",
		PetAboutName: "Fast geschafft! Darf ich fragen wie du heißt?",
		PetAboutEmail: "Hallo {name}! Wohin soll ich dir den Link schicken?",
		PetAboutPasskey: "Zum Schluss, {name}: ein Passkey – dein Schlüssel zu diesem Polly.",
		PetFieldName: "Ein Spitzname reicht – den sehen deine Freunde.",
		PetFieldEmail: "Hier schicke ich dir den Link. Deine Freunde sehen die Adresse nie.",
		PetFieldEmailNamed: "Danke, {name}! Hier schicke ich dir den Link – nur ich sehe die Adresse.",
		PetFieldPasskey: "Dein Passkey ist dein Schlüssel – ganz ohne Passwort.",
		PetPasskeyIsReady: "Dein Passkey ist bereit!",
		PetReadyToStart: "Alles fertig! Starte dein Polly.",
		PetSaved: "Gespeichert!",
		PetShareReady: "Spitze, dein Polly ist jetzt gestartet.",
		PetBallotFriend: "Hallo! In diesem Polly von {name} kannst du jetzt mit abstimmen.",
		PetBallotOwner: "Dein Polly läuft. Stimm doch auch selbst ab!",
		PetBallotNoOwner: "Hallo! Hier ist eine Frage an dich.",
		PetConfirm: "Sicher, {name}? Du kannst nur einmal abstimmen!",
		PetVoted: "Danke, {name}! Deine Stimme wurde gezählt.",
		PetVotedWaitForOwner: "Sobald {name} dieses Polly abschließt, siehst du hier den Gewinner.",
		PetWinner: "Und der Gewinner ist: {title}!",
		PetNoVotes: "Hier hat niemand abgestimmt. Vielleicht beim nächsten Mal!",
		PetNameMissing: "Sag mir bitte erst, wie du heißt.",
		PetNameTaken: "Sorry, diesen Namen gibt es hier schon. Bitte wähle einen anderen.",
		// The creator changed the polly while this ballot was open (polly-page.vue, takeChangedPolly)
		PollyChangedMeanwhile: "Bitte entschuldige, {name} hat dieses Polly gerade noch mal geändert. Ich lade es neu.",
		PetNeedPasskey: "Dafür brauche ich deinen Passkey. Tipp auf Passkey erstellen.",
		PetPasskeyFailed: "Das hat nicht geklappt. Versuch den Passkey noch einmal.",
		PetCopyFailed: "Das Kopieren hat nicht geklappt. Halte den Link gedrückt.",
		PetGiggle1: "Uhhuu, Uhuu!",
		PetGiggle2: "Du suchst meine Aufmerksamkeit? Doch wahre Weisheit findet man duch aufmerksames Zuhören.",
		PetGiggle3: "Nur die Oberfläche zu berühren ist einfach, doch wirklich zu verstehen kostet ein Leben lang!",
		PetMoved: "{title} ist jetzt auf Platz {n}",
		PetQuestionLabel: "Deine Frage",
		PetOptionLabel: "Antwort {n}",
		PetRemoveOption: "Antwort {n} entfernen",
		PetHowTitle: "So funktioniert's",
		PetHow1: "Bring alle Antworten in deine Reihenfolge – deinen Liebling nach oben.",
		PetHow2: "Jeder stimmt genau einmal ab. Es gewinnt der Vorschlag mit der größten Zustimmung unter euch allen.",
		PetHow3: "Alle sehen, wer abgestimmt hat – aber niemals wie jemand abgestimmt hat.",
		PetFlipInfo: "Info",
		PetFlipBack: "Zurück",
		PetCardOpen: "Antworten anzeigen",
		PetCardClose: "Antworten verbergen",
		PetNotebook: "Athenas Notizbuch",
		StepVote: "Abstimmung",
		PetRankHint: "Sortiere alle Antworten wie es dir gefällt.\nDein Favorit ganz oben.",
		PetRankList: "Deine Rangliste",
		PetRankUp: "{title} nach oben",
		PetRankDown: "{title} nach unten",
		PetConfirmVoteButton: "Diese Stimme jetzt abgeben.",
		PetVoteBack: "zurück",
		PetShareTitle: "Teile diesen einen Link",
		PetQrAlt: "QR-Code mit dem Link zu diesem Polly",
		PetShareLinkButton: "Link teilen",
		PetFinishPollyLong: "Polly beenden",
		PetEditPollyLong: "Polly bearbeiten",
		PetOnlyForYou: "Nur für dich",
		PetEditBeforeLink: "So lange noch niemand abgestimmt hat, kannst du dein",
		PetEditLinkText: "Polly noch bearbeiten",
		PetVoteNow: "Jetzt abstimmen",
		PetDone: "Fertig",
		PetLinkMailed: "Ich habe dir auch eine E-Mail mit allen Infos geschickt.",
		PetVotersTitle: "Bisher abgestimmt haben:",
		PetNobodyYet: "Noch niemand.",
		PetYourVoteIn: "Danke. Deine Stimme wurde gezählt.",
		PetRegisterAfterVote: "Mit einem Passkey kannst du später wieder kommen und selbst dein eigenes Polly starten.",
		PetConfirmFinish: "Wirklich beenden? Dann kann niemand mehr abstimmen.",
		PetYesFinish: "Ja, beenden",
		PetOtherOptions: "Die anderen Vorschläge waren:",
		PetStartOwn: "Eigenes Polly starten",
		PetStartAnother: "Weiteres Polly starten",
	},
}

export default messages
